import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, InputNumber, Modal, Progress, Select, Spin, Table, Tag, Typography } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DownloadOutlined,
  FileExcelOutlined,
  PlusCircleFilled,
  UploadOutlined,
  WarningFilled,
} from "@ant-design/icons";
import {
  DEFAULT_SUFFIX,
  SUFFIXES,
  TRADING_COLUMNS,
  getTradingRowProblems,
} from "../../../../utils/tradingExcelImport";

const { Text } = Typography;

const formatNumber = (value) =>
  value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN");

const FORMAT_ROWS = [
  { name: "Location", need: "Always", example: "Delhi Gate Circle", note: "Used to find the inventory. If it already exists as a trading inventory it is reused, otherwise it is created." },
  { name: "Start Date / End Date", need: "Always", example: "15-08-2026 / 04-09-2026", note: "DD-MM-YYYY (Excel dates also work)." },
  { name: "Town", need: "New inventory", example: "Palanpur", note: "Saved as both City and Area." },
  { name: "Media Vehicle", need: "New inventory", example: "Hoarding", note: "Gantry, Unipole, Hoarding, Cantilever, LED or Kiosk." },
  { name: "Size (W) / Size (H)", need: "New inventory", example: "25 / 25", note: "In feet." },
  { name: "Media Type", need: "New inventory", example: "NL", note: "FL, BL or NL (illumination)." },
  { name: "Rate PM", need: "New inventory", example: "25,000", note: "Rate per month. Used as the rate on the BDF line." },
  { name: "Buying Rate PM", need: "New inventory", example: "12,000", note: "Buying cost per month. Can also be typed on the next screen." },
  { name: "Printing / Mounting Charges", need: "Optional", example: "6,250 / 3,125", note: "Blank = standard charge for the size." },
];

const StatBox = ({ label, value, color, icon }) => (
  <div
    style={{
      flex: 1,
      minWidth: 130,
      padding: "10px 14px",
      border: "1px solid #f0f0f0",
      borderLeft: `4px solid ${color}`,
      borderRadius: 6,
      background: "#fff",
    }}
  >
    <div style={{ color: "#8c8c8c", fontSize: 12 }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 22, fontWeight: 600, color }}>{value}</div>
  </div>
);

const TradingExcelImportModal = ({
  open,
  status,
  fileName,
  error,
  foundHeaders,
  info,
  plan,
  edits,
  vendors,
  showMissing,
  progress,
  onEdit,
  onEditAll,
  onImport,
  onCancel,
  onDownloadTemplate,
  onChooseFile,
}) => {
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [bulkVendor, setBulkVendor] = useState(undefined);
  const [bulkSuffix, setBulkSuffix] = useState(undefined);

  useEffect(() => {
    if (open) {
      setOnlyProblems(false);
      setBulkVendor(undefined);
      setBulkSuffix(undefined);
    }
  }, [open, fileName]);

  const planRows = plan?.rows;
  const visibleRows = useMemo(() => {
    const all = planRows || [];
    return onlyProblems ? all.filter((r) => r.status === "error") : all;
  }, [planRows, onlyProblems]);

  const vendorOptions = useMemo(
    () => (vendors || []).map((v) => ({ value: v.name, label: v.name, gst: v.gstNumber })),
    [vendors]
  );
  const vendorProps = {
    showSearch: true,
    allowClear: true,
    placeholder: "Select vendor",
    options: vendorOptions,
    optionRender: (option) => (
      <div>
        <div style={{ fontWeight: 600 }}>{option.data.label}</div>
        <div style={{ fontSize: 12, color: "#888" }}>{option.data.gst || "No GST Number"}</div>
      </div>
    ),
    filterOption: (input, option) =>
      `${option?.label || ""} ${option?.gst || ""}`.toLowerCase().includes(input.toLowerCase()),
  };
  const suffixOptions = SUFFIXES.map((s) => ({ value: s, label: s }));

  const problems = useMemo(() => getTradingRowProblems(plan, edits), [plan, edits]);
  const problemKeys = useMemo(
    () => new Set(problems.map((p) => `${p.groupKey}:${p.field}`)),
    [problems]
  );
  const missingStyle = (row, field) =>
    showMissing && problemKeys.has(`${row.groupKey}:${field}`) ? { borderColor: "#cf1322" } : undefined;

  const columns = [
    { title: "Row", dataIndex: "rowNumber", key: "rowNumber", width: 50, align: "center" },
    {
      title: "Location",
      key: "location",
      render: (_, r) => (
        <div>
          <div>{r.location || "-"}</div>
          {r.kind === "new" && r.status === "ready" && (
            <div>
              <Tag color="purple" icon={<PlusCircleFilled />} style={{ marginRight: 6 }}>
                New
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {r.newInv.city} · {r.newInv.mediaVehicle} · {r.newInv.width}x{r.newInv.height} · {r.newInv.illu}
              </Text>
            </div>
          )}
          {r.kind === "existing" && r.code && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Existing · {r.code}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Vendor",
      key: "vendor",
      width: 170,
      render: (_, r) => {
        if (r.status !== "ready") return "-";
        if (r.kind === "existing") return r.vendor || "-";
        const value = (edits[r.groupKey] || {}).vendor ?? r.vendor;
        return (
          <Select
            {...vendorProps}
            size="small"
            style={{ width: "100%" }}
            status={showMissing && problemKeys.has(`${r.groupKey}:vendor`) ? "error" : undefined}
            value={value || undefined}
            onChange={(v) => onEdit(r.groupKey, { vendor: v })}
          />
        );
      },
    },
    {
      title: "Suffix",
      key: "suffix",
      width: 80,
      render: (_, r) => {
        if (r.status !== "ready") return "-";
        if (r.kind === "existing") return r.suffix || "-";
        const value = (edits[r.groupKey] || {}).suffix ?? r.suffix ?? DEFAULT_SUFFIX;
        return (
          <Select
            size="small"
            style={{ width: "100%" }}
            options={suffixOptions}
            value={value}
            onChange={(v) => onEdit(r.groupKey, { suffix: v })}
          />
        );
      },
    },
    {
      title: "Buying Rate PM",
      key: "buyingRate",
      width: 110,
      align: "right",
      render: (_, r) => {
        if (r.status !== "ready" || r.kind !== "new") return "-";
        const value = (edits[r.groupKey] || {}).purchaseRate ?? r.newInv.purchaseRate;
        return (
          <InputNumber
            size="small"
            min={0}
            controls={false}
            style={{ width: "100%", ...(missingStyle(r, "purchaseRate") || {}) }}
            status={showMissing && problemKeys.has(`${r.groupKey}:purchaseRate`) ? "error" : undefined}
            value={value ?? null}
            onChange={(v) => onEdit(r.groupKey, { purchaseRate: v })}
          />
        );
      },
    },
    {
      title: "Printing",
      key: "printingCharge",
      width: 70,
      align: "right",
      render: (_, r) => (r.defaults?.printingCharge ? <Text type="secondary">default</Text> : formatNumber(r.printingCharge)),
    },
    {
      title: "Mounting",
      key: "mountingCharge",
      width: 70,
      align: "right",
      render: (_, r) => (r.defaults?.mountingCharge ? <Text type="secondary">default</Text> : formatNumber(r.mountingCharge)),
    },
    {
      title: "Rate PM",
      key: "specialRatePerMonth",
      width: 80,
      align: "right",
      render: (_, r) => formatNumber(r.specialRatePerMonth),
    },
    {
      title: "Start",
      dataIndex: "startDate",
      key: "startDate",
      width: 105,
      render: (v) => <span style={{ whiteSpace: "nowrap" }}>{v || "-"}</span>,
    },
    {
      title: "End",
      dataIndex: "endDate",
      key: "endDate",
      width: 105,
      render: (v) => <span style={{ whiteSpace: "nowrap" }}>{v || "-"}</span>,
    },
    {
      title: "Amount",
      key: "amount",
      width: 80,
      align: "right",
      render: (_, r) =>
        r.amount === null && r.status === "ready" ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            after dates
          </Text>
        ) : (
          formatNumber(r.amount)
        ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) =>
        r.status === "ready" ? (
          <div>
            <Tag color="green" icon={<CheckCircleFilled />}>
              Ready
            </Tag>
            {r.warnings.map((w) => (
              <div key={w} style={{ color: "#d48806", fontSize: 12 }}>
                <WarningFilled /> {w}
              </div>
            ))}
          </div>
        ) : (
          <div>
            <Tag color="red" icon={<CloseCircleFilled />}>
              Problem
            </Tag>
            {r.errors.map((e) => (
              <div key={e} style={{ color: "#cf1322", fontSize: 12 }}>
                {e}
              </div>
            ))}
          </div>
        ),
    },
  ];

  const readyCount = plan?.readyCount || 0;
  const errorCount = plan?.errorCount || 0;
  const newCount = plan?.newCount || 0;
  const existingCount = plan?.existingCount || 0;
  const importing = status === "importing";

  const footer = [
    <Button key="template" icon={<DownloadOutlined />} onClick={onDownloadTemplate} style={{ float: "left" }} disabled={importing}>
      Download template
    </Button>,
    status !== "loading" && status !== "intro" && !importing && (
      <Button key="another" icon={<UploadOutlined />} onClick={onChooseFile}>
        Choose another file
      </Button>
    ),
    <Button key="cancel" onClick={onCancel} disabled={importing}>
      Cancel
    </Button>,
    status === "intro" && (
      <Button key="choose" type="primary" icon={<UploadOutlined />} onClick={onChooseFile}>
        Choose Excel file
      </Button>
    ),
    (status === "preview" || importing) && (
      <Button key="import" type="primary" loading={importing} disabled={readyCount === 0 || importing} onClick={onImport}>
        {newCount > 0
          ? `Import ${readyCount} (create ${newCount} new)`
          : `Import ${readyCount} inventor${readyCount === 1 ? "y" : "ies"}`}
        {errorCount > 0 ? `, skip ${errorCount}` : ""}
      </Button>
    ),
  ].filter(Boolean);

  return (
    <Modal
      title="Import trading inventories from Excel"
      open={open}
      onCancel={importing ? undefined : onCancel}
      closable={!importing}
      maskClosable={false}
      keyboard={!importing}
      width="min(1500px, 96vw)"
      footer={footer}
      destroyOnClose
    >
      {fileName && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">File: </Text>
          <Text strong>{fileName}</Text>
          {info?.sheetName && <Text type="secondary"> (sheet "{info.sheetName}")</Text>}
        </div>
      )}

      {status === "intro" && (
        <div>
          <Text type="secondary">
            Upload a .xlsx, .xls or .csv file (max 5 MB, up to 1000 rows). Column order and exact heading wording do
            not matter. Locations that already exist as trading inventories are reused; new ones are created when you
            import. You choose the vendor and suffix (default HO) for each new one on the next screen.
          </Text>
          <Table
            size="small"
            style={{ marginTop: 12 }}
            rowKey="name"
            pagination={false}
            dataSource={FORMAT_ROWS}
            columns={[
              {
                title: "Column",
                dataIndex: "name",
                width: 230,
                render: (v) => (
                  <span>
                    <FileExcelOutlined style={{ color: "#389e0d", marginRight: 6 }} />
                    <Text strong>{v}</Text>
                  </span>
                ),
              },
              {
                title: "Needed",
                dataIndex: "need",
                width: 120,
                render: (v) => <Tag color={v === "Always" ? "red" : v === "Optional" ? "default" : "purple"}>{v}</Tag>,
              },
              { title: "Example", dataIndex: "example", width: 190 },
              { title: "Notes", dataIndex: "note" },
            ]}
          />

          <div style={{ marginTop: 14 }}>
            <Text strong>What your sheet should look like</Text>
          </div>
          <div style={{ overflowX: "auto", marginTop: 6 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  {TRADING_COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      style={{
                        background: "#fff566",
                        border: "1px solid #d9d9d9",
                        padding: "4px 8px",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {["Palanpur", "Delhi Gate Circle", "Hoarding", "25", "25", "NL", "25,000", "12,000", "6,250", "3,125", "15-08-2026", "04-09-2026"].map(
                    (v, i) => (
                      <td key={i} style={{ border: "1px solid #e8e8e8", padding: "4px 8px", whiteSpace: "nowrap" }}>
                        {v}
                      </td>
                    )
                  )}
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 8, color: "#8c8c8c", fontSize: 12 }}>
            Columns such as Sr. No and Sq Ft are ignored.
          </div>
        </div>
      )}

      {status === "loading" && (
        <div style={{ textAlign: "center", padding: "56px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 15, fontWeight: 500 }}>Reading your file...</div>
          <div style={{ marginTop: 4, color: "#8c8c8c", fontSize: 13 }}>
            Checking which locations already exist as trading inventories.
          </div>
        </div>
      )}

      {status === "error" && (
        <div>
          <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
          {foundHeaders && foundHeaders.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Columns found: </Text>
              {foundHeaders.map((h) => (
                <Tag key={h}>{h}</Tag>
              ))}
            </div>
          )}
          <div>
            <Text strong>Expected columns </Text>
            <Text type="secondary">(any order; * required)</Text>
            <div style={{ marginTop: 6 }}>
              {TRADING_COLUMNS.map((c) => (
                <Tag key={c.key} color={c.required ? "blue" : "default"}>
                  {c.label}
                  {c.required ? " *" : ""}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      )}

      {importing && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Progress
            type="circle"
            percent={progress && progress.total ? Math.round((progress.done / progress.total) * 100) : 100}
          />
          <div style={{ marginTop: 16, fontSize: 15, fontWeight: 500 }}>
            {progress && progress.total > 0
              ? `Creating new trading inventories (${progress.done} of ${progress.total})...`
              : "Adding inventories..."}
          </div>
          <div style={{ marginTop: 4, color: "#8c8c8c", fontSize: 13 }}>Please keep this window open.</div>
        </div>
      )}

      {status === "preview" && (
        <div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <StatBox label="Total rows" value={readyCount + errorCount} color="#595959" />
            <StatBox label="Existing" value={existingCount} color="#1677ff" icon={<CheckCircleFilled />} />
            <StatBox label="New (will be created)" value={newCount} color="#722ed1" icon={<PlusCircleFilled />} />
            <StatBox
              label="Problems (skipped)"
              value={errorCount}
              color={errorCount > 0 ? "#cf1322" : "#8c8c8c"}
              icon={errorCount > 0 ? <CloseCircleFilled /> : null}
            />
          </div>

          {showMissing && problems.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${problems.length} thing${problems.length === 1 ? "" : "s"} still needed: pick a vendor and enter a buying rate for every new inventory.`}
            />
          )}

          {newCount > 0 && (
            <div
              style={{
                marginBottom: 12,
                padding: "10px 12px",
                border: "1px solid #f0f0f0",
                borderRadius: 6,
                background: "#fafafa",
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Text strong>Set for all new inventories:</Text>
              <Select
                {...vendorProps}
                style={{ width: 260 }}
                value={bulkVendor}
                onChange={(v) => {
                  setBulkVendor(v);
                  if (v) onEditAll({ vendor: v });
                }}
              />
              <Select
                placeholder="Suffix"
                style={{ width: 100 }}
                options={suffixOptions}
                value={bulkSuffix}
                onChange={(v) => {
                  setBulkSuffix(v);
                  if (v) onEditAll({ suffix: v });
                }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                You can still change each row below. Suffix is HO unless changed.
              </Text>
            </div>
          )}

          {(info?.warnings || []).map((w) => (
            <Alert key={w} type="warning" showIcon message={w} style={{ marginBottom: 8 }} />
          ))}

          {errorCount > 0 && (
            <Checkbox checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} style={{ marginBottom: 8 }}>
              Show only rows with problems
            </Checkbox>
          )}

          <Table
            size="small"
            rowKey="rowNumber"
            columns={columns}
            onRow={(r) => (r.status === "error" ? { style: { background: "#fff1f0" } } : {})}
            dataSource={visibleRows}
            // All rows on one page; the table scrolls inside the dialog (header stays put).
            pagination={false}
            scroll={{ y: "max(240px, calc(100vh - 480px))" }}
          />
        </div>
      )}
    </Modal>
  );
};

export default TradingExcelImportModal;
